/**
 * CustomClassModal component renders a modal dialog for selecting and inputting custom class data.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {boolean} props.open - Determines if the modal is open.
 * @param {CustomClassModalData} props.customClassData - The data for the custom class.
 * @param {() => void} props.handleClose - Function to handle closing the modal.
 * @param {'main' | 'rightBorder' | 'leftBorder'} props.road - Specifies the road type.
 * @param {(road: 'main' | 'rightBorder' | 'leftBorder', data: CustomClassModalData) => void} [props.handleSaveDataFromModal] - Optional function to handle saving data from the modal.
 *
 * @returns {JSX.Element} The rendered CustomClassModal component.
 *
 * @example
 * <CustomClassModal
 *   open={true}
 *   customClassData={customClassData}
 *   handleClose={handleClose}
 *   road="main"
 *   handleSaveDataFromModal={handleSaveDataFromModal}
 * />
 *
 * @remarks
 * The component uses Material-UI for rendering the modal and form elements. It also uses the `useTranslation` hook from `react-i18next` for internationalization.
 *
 * The form fields and validation logic are dynamically generated based on the selected class type ('M', 'C', or 'P'). The form values and errors are managed using React's `useState` hook.
 *
 * The `generateCustomClassData` function creates the appropriate custom class data object based on the selected class type and form values.
 *
 * The `saveFormData` function validates the form, generates the custom class data, and calls the `handleSaveDataFromModal` function with the new data.
 */

import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, TextField, ButtonGroup, Tooltip } from "@mui/material";
import type { CCustomClassData, CustomClassModalData, MCustomClassData, PCustomClassData, PreSelectedClass } from "types";
import { ceilToTwoDecimals, generateFormExistingData, getClassTargetValues, isCustomClassModalDataEmpty } from "utils/helpers";

/**
 * Custom class modal props
 */
interface ModalProps {
  open: boolean;
  customClassData: CustomClassModalData;
  preSelectedClass: PreSelectedClass;
  road: "main" | "rightBorder" | "leftBorder";
  handleClose: () => void;
  handleSaveDataFromModal?: (road: "main" | "rightBorder" | "leftBorder", data: CustomClassModalData) => void;
}

const isUoOrUlBiggerThanOne = (field: string, value: string) => (field === "Uo" || field === "Ul") && Number(value) > 1;

const INIT_VALUES = {
  M: { L̄m: "2", Uo: "0.4", Ul: "0.7", fTI: "10", REI: "0.35" },
  C: { Ēm: "50", Uo: "0.4", fTI: "15" },
  P: { Ēm: "15", Ēmin: "3", "Ev,min": "5", "Esc,min": "3", fTI: "20" },
} as const;

const FORM_FIELDS = {
  M: ["L̄m", "Uo", "Ul", "fTI"],
  C: ["Ēm", "Uo"],
  P: ["Ēm", "Ēmin"],
} as const;

const Form: React.FC<{
  selectedClass: "M" | "C" | "P";
  formValues: Record<string, string>;
  formErrors: Record<string, boolean>;
  onInputChange: (field: string, value: string, isFormatted: boolean) => void;
}> = ({ selectedClass, formValues, formErrors, onInputChange }) => {
  const { t } = useTranslation();
  const formFields = FORM_FIELDS[selectedClass];

  return (
    <Box
      mt="var(--rhythm)"
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "calc(1.5 * var(--rhythm))",
      }}
    >
      {formValues &&
        formFields.map((field, index) => (
          <TextField
            key={field}
            required
            fullWidth
            size="small"
            type="number"
            variant="outlined"
            label={field}
            value={formValues[field] || ""}
            error={!!formErrors[field]}
            helperText={
              formErrors[field]
                ? isUoOrUlBiggerThanOne(field, formValues[field])
                  ? t("This field value must be less than 1")
                  : t("This field is required and must be positive")
                : ""
            }
            InputLabelProps={{
              required: false, // Hides the asterisk
            }}
            inputProps={{
              max: field === "Uo" || field === "Ul" ? 1 : 9999,
              min: 0,
              step: 0.01,
            }}
            // @todo: Uncomment once upgraded to MUI v7
            // slotProps={{
            //   htmlInput: {
            //     max: field === "Uo" || field === "Ul" ? 1 : 9999,
            //     min: 0,
            //     step: 0.01,
            //   },
            //   inputLabel: {
            //     required: false, // Hides the asterisk
            //   },
            // }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => onInputChange(field, e.target.value, true)}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(field, e.target.value, false)}
            sx={{ gridColumn: index % 2 === 0 ? "span 1" : "span 1" }}
          />
        ))}
    </Box>
  );
};

export const CustomClassModal: React.FC<ModalProps> = ({ open, road, customClassData, preSelectedClass, handleClose, handleSaveDataFromModal }) => {
  const { t } = useTranslation();
  const roadClass = preSelectedClass?.split("")[0] as "M" | "C" | "P";
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [selectedClass, setSelectedClass] = useState<"M" | "C" | "P">(roadClass);

  const initFormValues = useMemo<Record<string, string>>(() => {
    const isCustomDataEmpty = isCustomClassModalDataEmpty(customClassData, road === "main", road === "leftBorder");
    const preSelectedValues = getClassTargetValues(roadClass, preSelectedClass || "");

    return !isCustomDataEmpty
      ? generateFormExistingData(customClassData, road === "main", road === "leftBorder")
      : (preSelectedValues ?? INIT_VALUES[roadClass]);
  }, [customClassData, preSelectedClass, road, roadClass]);

  useEffect(() => {
    if (!open) return;

    setSelectedClass(roadClass);
    setFormValues(initFormValues);
    setFormErrors({});
  }, [initFormValues, open, roadClass]);

  const handleInputChange = useCallback((field: string, value: string, isFormatted: boolean) => {
    const formattedValue = isFormatted ? ceilToTwoDecimals(value) : value;
    setFormValues((prevValues) => ({ ...prevValues, [field]: formattedValue }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [field]: !formattedValue || Number(formattedValue) < 0 || isUoOrUlBiggerThanOne(field, formattedValue),
    }));
  }, []);

  const clearFormFields = useCallback(() => {
    setFormValues({});
    setFormErrors({});
  }, []);

  const handleCloseModal = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const parseValue = useCallback((field: string) => Number.parseFloat(formValues[field] || "") || 0, [formValues]);

  const generateCustomClassData = useCallback(() => {
    switch (selectedClass) {
      case "M":
        return {
          class_type: "M",
          Lm_av: parseValue("L̄m"),
          L_uo: parseValue("Uo"),
          L_ul: parseValue("Ul"),
          fTI: parseValue("fTI"),
          Rei: parseValue("REI"),
        } as MCustomClassData;
      case "C":
        return {
          class_type: "C",
          Em_av: parseValue("Ēm"),
          E_uo: parseValue("Uo"),
          fTI: parseValue("fTI"),
        } as CCustomClassData;
      case "P":
      default:
        return {
          class_type: "P",
          Em_av: parseValue("Ēm"),
          Em_min: parseValue("Ēmin"),
          fTI: parseValue("fTI"),
          Evm_min: parseValue("Ev,min"),
          Escm_min: parseValue("Esc,min"),
        } as unknown as PCustomClassData;
    }
  }, [parseValue, selectedClass]);

  const handleChangeClass = useCallback((cls: "M" | "C" | "P") => {
    setSelectedClass(cls);
    clearFormFields();
  }, [clearFormFields]);

  const handleSelectMClass = useCallback(() => {
    handleChangeClass("M");
  }, [handleChangeClass]);

  const handleSelectCClass = useCallback(() => {
    handleChangeClass("C");
  }, [handleChangeClass]);

  const handleSelectPClass = useCallback(() => {
    handleChangeClass("P");
  }, [handleChangeClass]);

  const saveFormData = useCallback(() => {
    const generatedClass = generateCustomClassData();
    const newCustomClassData = {
      ...customClassData,
      mainCustomClass: road === "main" ? generatedClass : customClassData.mainCustomClass,
      rightBorderCustomClass: road === "rightBorder" ? generatedClass : customClassData.rightBorderCustomClass,
      leftBorderCustomClass: road === "leftBorder" ? generatedClass : customClassData.leftBorderCustomClass,
    };
    handleSaveDataFromModal?.(road, newCustomClassData);
    clearFormFields();
    handleClose();
  }, [clearFormFields, customClassData, generateCustomClassData, handleClose, handleSaveDataFromModal, road]);

  const canSave = useMemo(() => {
    const requiredFields = FORM_FIELDS[selectedClass];
    let isSameAsInitial = true;

    const isFormValid = requiredFields?.every((field) => {
      isSameAsInitial = isSameAsInitial && Number.parseFloat(formValues?.[field]) === Number.parseFloat(initFormValues?.[field]);

      const value = formValues?.[field];
      const noErrors = formErrors[field] === undefined || formErrors[field] === false;

      return value !== undefined && value !== "" && Number(value) >= 0 && noErrors;
    });

    return isFormValid && !isSameAsInitial;
  }, [formErrors, formValues, initFormValues, selectedClass]);

  return (
    <Dialog open={open} fullWidth maxWidth="xs" onClose={handleClose}>
      <DialogTitle>{t("Choose custom values")}</DialogTitle>

      <DialogContent>
        <ButtonGroup fullWidth size="small" variant="outlined" sx={{ mb: "var(--rhythm)" }}>
          {road === "main" && (
            <Button variant={selectedClass === "M" ? "contained" : "outlined"} disabled={selectedClass !== "M"} onClick={handleSelectMClass}>
              {t("M-Class")}
            </Button>
          )}
          <Button variant={selectedClass === "C" ? "contained" : "outlined"} disabled={selectedClass !== "C"} onClick={handleSelectCClass}>
            {t("C-Class")}
          </Button>
          <Button variant={selectedClass === "P" ? "contained" : "outlined"} disabled={selectedClass !== "P"} onClick={handleSelectPClass}>
            {t("P-Class")}
          </Button>
        </ButtonGroup>

        <Form selectedClass={selectedClass} formValues={formValues} formErrors={formErrors} onInputChange={handleInputChange} />
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={handleCloseModal}>
          {t("Cancel")}
        </Button>
        <Tooltip arrow disableInteractive title={t("To enable the save button, all the value must be added, and it should not equal the default values")}>
          <Box>
            <Button variant="contained" disabled={!canSave} onClick={saveFormData}>
              {t("Save")}
            </Button>
          </Box>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};
